import { createFileRoute, Link } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BookMarked, CheckCircle2, Clock, ArrowRight, PlayCircle } from "lucide-react";
import { getCollections, type CollectionSummary } from "@/lib/content.functions";
import { resolveImageWithFallback } from "@/lib/asset-map";
import { useAllCollectionProgress } from "@/hooks/use-collection-progress";
import { usePageView } from "@/hooks/use-page-view";
import { SaveItemButton } from "@/components/SaveItemButton";
import { ShareButtons } from "@/components/ShareButtons";
import { useLocale } from "@/hooks/use-locale";
import { countCopy, listCopy, seriesSummary, seriesTitle } from "@/lib/list-locales";


const READ_MIN_PER = 4;

export const Route = createFileRoute("/collections/")({
  head: () => ({
    meta: [
      { title: "Series - Smartasaurus discoveries in order" },
      { name: "description", content: "Every Smartasaurus series: curated, multi-part sets of discoveries you can binge from episode one." },
      { property: "og:title", content: "Series - Smartasaurus" },
      { property: "og:description", content: "Curated series of Smartasaurus discoveries. Pick one and start at episode one." },
      { property: "og:url", content: "https://smartasaurus.com/collections" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Series - Smartasaurus" },
      { name: "twitter:description", content: "Curated series of Smartasaurus discoveries. Pick one and start at episode one." },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/collections" }],
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["collections-index"],
      queryFn: () => getCollections(),
    }),
  component: CollectionsPage,
  errorComponent: ({ error }: ErrorComponentProps) => <div className="p-8 text-center text-muted-foreground">{(error as Error).message}</div>,
  notFoundComponent: () => <div className="p-8 text-center">No series yet.</div>,
});

import { SERIES } from "@/lib/series-taxonomy";

const ORDER = SERIES.map((s) => s.slug);

function CollectionsPage() {
  const data = Route.useLoaderData() as { items: CollectionSummary[] };
  const progress = useAllCollectionProgress();
  const [lang] = useLocale();
  const copy = listCopy(lang);
  const counts = countCopy(lang);
  usePageView("collection");
  const bySlug = useMemo(() => {
    const m = new Map<string, CollectionSummary>();
    for (const c of data.items) m.set(c.slug, c);
    return m;
  }, [data.items]);

  const sorted = useMemo(
    () => ORDER.map((slug) => bySlug.get(slug)).filter((c): c is CollectionSummary => Boolean(c)),
    [bySlug],
  );

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-card to-background py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
             <div data-no-translate className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
               <BookMarked className="h-3.5 w-3.5" /> {copy.series}
            </div>
             <h1 data-no-translate className="mt-3 font-display text-5xl uppercase leading-[0.95] md:text-7xl">
               Smartasaurus <span className="text-gradient-curiosity">{copy.series}</span>.
            </h1>
             <p data-no-translate className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
               {copy.seriesIntro}
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
             <div data-no-translate className="mb-8 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
               {counts.series(sorted.length)}
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">

              {sorted.map((c) => {
                const minutes = c.count * READ_MIN_PER;
                const img = resolveImageWithFallback(c.first_image, "collection", c.slug);
                const visited = progress[c.slug] ?? [];
                const visitedCount = Math.min(c.count, visited.length);
                const pct = c.count > 0 ? Math.round((visitedCount / c.count) * 100) : 0;
                const started = visitedCount > 0;
                const complete = visitedCount === c.count && c.count > 0;
                 const ctaLabel = complete ? copy.revisit : started ? copy.continue : copy.startSeries;
                 const localizedTitle = seriesTitle(lang, c.title);
                return (
                  <article
                    key={c.id}
                    className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/40"
                  >
                    <Link
                      to="/collections/$slug"
                      params={{ slug: c.slug }}
                       aria-label={`${copy.openSeries}: ${localizedTitle}`}
                      className="relative block aspect-[5/3] overflow-hidden"
                    >
                      {img ? (
                        <img src={img} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      ) : (
                        <div className="h-full w-full bg-gradient-curiosity opacity-40" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                      <div className="absolute right-3 top-3 rounded-full bg-primary/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                         <span data-no-translate>{counts.episodes(c.count)}</span>
                      </div>
                      {complete && (
                        <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                           <CheckCircle2 className="h-3 w-3" /> <span data-no-translate>{copy.complete}</span>
                        </div>
                      )}
                      {started && !complete && (
                        <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary backdrop-blur">
                           <PlayCircle className="h-3 w-3" /> <span data-no-translate>{copy.inProgress}</span>
                        </div>
                      )}
                    </Link>
                    <div className="flex flex-1 flex-col p-5">
                      <Link to="/collections/$slug" params={{ slug: c.slug }}>
                         <h2 data-no-translate className="break-words font-display text-2xl uppercase leading-tight text-foreground transition group-hover:text-primary">{localizedTitle}</h2>
                      </Link>
                      {c.description ? (
                         <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{seriesSummary(lang, c.slug, c.description)}</p>
                      ) : <p className="mt-2 min-h-[2.6em]" />}
                      <div className="mt-3 flex items-center gap-3 text-[11px] uppercase tracking-widest text-muted-foreground">
                         <span data-no-translate className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {counts.minutes(minutes)}</span>
                         <span data-no-translate>{counts.episodes(c.count)}</span>
                      </div>
                      {/* Real per-visitor progress, hydrated from localStorage. */}
                      <div className="mt-3 min-h-[2.5rem]">
                        {c.count > 0 && (
                          <>
                            <div className="h-1 w-full overflow-hidden rounded-full bg-border">
                              <div className="h-full bg-gradient-curiosity transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                               <span data-no-translate>{counts.progress(visitedCount, c.count)}</span>
                              <span>{pct}%</span>
                            </div>
                          </>
                        )}
                      </div>
                      {/* Single primary CTA. Save + Share become icon-only affordances so the
                          episode grid stays visually calm. */}
                      <div className="mt-auto flex items-center justify-between gap-2 pt-4">
                        {c.first_slug ? (
                          <Link
                            to="/collections/$slug"
                            params={{ slug: c.slug }}
                            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider text-primary-foreground hover:brightness-110"
                          >
                             <span data-no-translate>{ctaLabel}</span> <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        ) : (
                           <div data-no-translate className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{copy.comingSoon}</div>
                        )}
                        <div className="flex items-center gap-1">
                          <SaveItemButton kind="collection" id={c.id} compact />
                          <ShareButtons url={`https://smartasaurus.com/collections/${c.slug}`} title={c.title} compact />
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>


            <div className="mt-14 rounded-2xl border border-border bg-card p-8 text-center md:p-12">
               <h3 data-no-translate className="font-display text-2xl uppercase md:text-3xl">{copy.moreSeries}</h3>
               <p data-no-translate className="mt-3 text-sm text-muted-foreground md:text-base">
                 {copy.moreSeriesCopy}
              </p>
              <a
                href="https://www.instagram.com/channel/Abb0uo2Ci9GzCQmM/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-foreground transition hover:brightness-110"
              >
                Join the Broadcast Channel <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
